<div
  class="list-item-container-wrapper"
  [class.embedded-in-collapsible]="embeddedInCollapsible"
  [class.show-background]="showBackground"
  [class.force-dark]="forceDarkMode"
  [ngStyle]="{ 'margin-bottom': marginBottom }"
  [class.bottom-border]="includeBottomBorder"
>
  <!-- Single item rendering (when pagination is disabled) -->
  @if (!enablePagination) {
    <div
      class="list-item hover dontgrow"
      [class.embedded-in-collapsible]="embeddedInCollapsible"
      [class.show-background]="showBackground"
      [class.force-dark]="forceDarkMode"
      [ngStyle]="{ 'margin-bottom': marginBottom }"
      [class.hover]="hoverState"
      [class.bottom-border]="includeBottomBorder"
    >
      @if (href) {
        <a
          [href]="href"
          class="list-item-container"
          [class.align-top]="alignTop"
          [class.expanded]="collapsible && !collapsed"
          (keydown.enter)="itemClick($event); $event.stopPropagation()"
          (keydown.space)="itemClick($event); $event.stopPropagation()"
          (click)="itemClick($event); $event.stopPropagation()"
        >
          <ng-container *ngTemplateOutlet="listItemContent"></ng-container>
        </a>
      }
      @if (!href) {
        <div
          class="list-item-container"
          [class.align-top]="alignTop"
          [class.expanded]="collapsible && !collapsed"
          (keydown.enter)="itemClick($event); $event.stopPropagation()"
          (keydown.space)="itemClick($event); $event.stopPropagation()"
          (click)="itemClick($event); $event.stopPropagation()"
        >
          <ng-container *ngTemplateOutlet="listItemContent"></ng-container>
        </div>
      }
      <ng-template #listItemContent>
        @if (collapsible) {
          <button class="collapse-icon">
            <fa-icon [icon]="collapsed ? 'chevron-right' : 'chevron-down'"></fa-icon>
          </button>
        }
        @if (showImage) {
          <img
            [src]="photo ? photo : './assets/images/default_avatar.jpg'"
            [ngStyle]="{ opacity: inactive ? '.3' : '1' }"
            class="item-image"
            [class.large]="largeImage"
            [class.round]="imageRound"
            [class.image-border]="imageBorder"
            [alt]="photoAlt || ''"
          />
        }
        @if (icon) {
          <fa-icon class="list-item-icon" [icon]="icon"></fa-icon>
        }
        <div class="main-data" (click)="itemClick($event); $event.stopPropagation()" [attr.tabindex]="href ? -1 : 0" [attr.role]="href ? 'link' : 'button'">
          <div class="d-flex align-items-center">
            @if (showUnreadBadge) {
              <span class="unread-badge d-block m-r-5">
                <span class="sr-only">{{ "general.status_unread" | translate }}</span>
              </span>
            }
            <fui-heading [text]="title" [color]="forceDarkMode ? 'white' : ''" [level]="3"></fui-heading>
          </div>
          @if (subDataHtml) {
            <div class="sub-data">
              <ng-container [ngTemplateOutlet]="subDataHtml"></ng-container>
            </div>
          }

          @if (subData && subData.length) {
            <span class="sub-data">
              @for (data of subData; track data) {
                <div class="muted" [innerHTML]="data"></div>
              }
            </span>
          }
          @if (metadata && metadata.length) {
            <div class="metadata">
              @for (data of metadata; track data) {
                <div class="tag">
                  <span class="title" [ngStyle]="{ color: data.color ? data.color : '' }">{{ data.title }}</span>
                  @if (!data.redirect) {
                    <span class="value" [ngStyle]="{ color: data.color ? data.color : '' }">{{ data.value }}</span>
                  }
                  @if (data.redirect) {
                    <a [routerLink]="data.redirect" target="_blank" rel="noopener noreferrer" class="value" [ngStyle]="{ color: data.color ? data.color : '' }" (click)="redirectClicked.emit()">
                      {{ data.value }}
                    </a>
                  }
                </div>
              }
            </div>
          }

          @if (description) {
            <div class="list-item-description">
              <div>{{ description }}</div>
            </div>
          }
        </div>
        <div class="right-data-buttons">
          <ng-container>
            <ng-content select=".button-content"></ng-content>
          </ng-container>

          @if (includeChevron) {
            <fa-icon aria-label="Select" icon="chevron-right" class="m-l-5" [ngbTooltip]="chevronTooltip" aria-hidden="true"></fa-icon>
          }
        </div>
      </ng-template>
      @if (collapsible) {
        <div class="collapsible" [class.d-none]="collapsed">
          <ng-content select=".collapsible-content"></ng-content>
        </div>
      }
    </div>
  }

  <!-- Multiple items rendering (when pagination is enabled) -->
  @if (enablePagination) {
    <div class="paginated-items-container">
      <!-- Pagination at top -->
      @if (paginationPosition === "top") {
        <div class="pagination-container pagination-top">
          @if (showPaginationInfo) {
            <div class="pagination-info">
              {{ paginationInfo }}
            </div>
          }
          <div class="pagination-controls">
            <button
              class="btn btn-sm btn-outline-secondary pagination-btn"
              [disabled]="currentPage === 1"
              (click)="onPageChange(currentPage - 1)"
              [attr.aria-label]="'general.previous_page' | translate"
            >
              <fa-icon icon="chevron-left"></fa-icon>
            </button>

            @for (page of pageNumbers; track page) {
              <button
                class="btn btn-sm pagination-btn"
                [class.btn-primary]="page === currentPage"
                [class.btn-outline-secondary]="page !== currentPage"
                (click)="onPageChange(page)"
                [attr.aria-label]="('general.page' | translate) + ' ' + page"
              >
                {{ page }}
              </button>
            }

            <button
              class="btn btn-sm btn-outline-secondary pagination-btn"
              [disabled]="currentPage === totalPages"
              (click)="onPageChange(currentPage + 1)"
              [attr.aria-label]="'general.next_page' | translate"
            >
              <fa-icon icon="chevron-right"></fa-icon>
            </button>
          </div>
        </div>
      }

      <!-- Individual items -->
      @for (paginatedItem of paginatedItems; track paginatedItem; let i = $index) {
        <div
          class="list-item hover dontgrow paginated-item"
          [class.embedded-in-collapsible]="embeddedInCollapsible"
          [class.show-background]="showBackground"
          [class.force-dark]="forceDarkMode"
          [ngStyle]="{ 'margin-bottom': marginBottom }"
          [class.hover]="hoverState"
          [class.bottom-border]="includeBottomBorder"
          [attr.data-item-index]="i"
        >
          <div
            class="list-item-container"
            [class.align-top]="alignTop"
            [class.expanded]="collapsible && !collapsed"
            (keydown.enter)="onItemClick(paginatedItem, $event); $event.stopPropagation()"
            (keydown.space)="onItemClick(paginatedItem, $event); $event.stopPropagation()"
            (click)="onItemClick(paginatedItem, $event); $event.stopPropagation()"
          >
            @if (collapsible) {
              <button class="collapse-icon">
                <fa-icon [icon]="collapsed ? 'chevron-right' : 'chevron-down'"></fa-icon>
              </button>
            }
            @if (showImage) {
              <img
                [src]="paginatedItem.photo || paginatedItem.image || paginatedItem.imageUrl || './assets/images/default_avatar.jpg'"
                [ngStyle]="{ opacity: paginatedItem.inactive ? '.3' : '1' }"
                class="item-image"
                [class.large]="largeImage"
                [class.round]="imageRound"
                [class.image-border]="imageBorder"
                [alt]="paginatedItem.photoAlt || paginatedItem.title || ''"
              />
            }
            @if (paginatedItem.icon || icon) {
              <fa-icon class="list-item-icon" [icon]="paginatedItem.icon || icon"></fa-icon>
            }
            <div class="main-data" (click)="onItemClick(paginatedItem, $event); $event.stopPropagation()" tabindex="0" [attr.role]="paginatedItem.href ? 'link' : 'button'">
              <div class="d-flex align-items-center">
                @if (paginatedItem.showUnreadBadge || showUnreadBadge) {
                  <span class="unread-badge d-block m-r-5">
                    <span class="sr-only">{{ "general.status_unread" | translate }}</span>
                  </span>
                }
                <fui-heading [text]="paginatedItem.title || ('general.untitled' | translate)" [color]="forceDarkMode ? 'white' : ''" [level]="3"></fui-heading>
              </div>
              @if (paginatedItem.subDataHtml || subDataHtml) {
                <div class="sub-data">
                  <ng-container [ngTemplateOutlet]="paginatedItem.subDataHtml || subDataHtml"></ng-container>
                </div>
              }

              @if ((paginatedItem.subData || subData) && (paginatedItem.subData || subData).length) {
                <span class="sub-data">
                  @for (data of paginatedItem.subData || subData; track data) {
                    <div class="muted" [innerHTML]="data"></div>
                  }
                </span>
              }
              @if ((paginatedItem.metadata || paginatedItem.displayMetadata || metadata) && (paginatedItem.metadata || paginatedItem.displayMetadata || metadata).length) {
                <div class="metadata">
                  @for (data of paginatedItem.metadata || paginatedItem.displayMetadata || metadata; track data) {
                    <div class="tag">
                      <span class="title" [ngStyle]="{ color: data.color ? data.color : '' }">{{ data.title }}</span>
                      @if (!data.redirect) {
                        <span class="value" [ngStyle]="{ color: data.color ? data.color : '' }">{{ data.value }}</span>
                      }
                      @if (data.redirect) {
                        <a [routerLink]="data.redirect" target="_blank" rel="noopener noreferrer" class="value" [ngStyle]="{ color: data.color ? data.color : '' }" (click)="redirectClicked.emit()">
                          {{ data.value }}
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            </div>
            <div class="right-data-buttons">
              <ng-container>
                <!-- Button content for paginated items -->
                <button
                  type="button"
                  class="circle-icon-btn m-r-10 bg-transparent border-0 p-0"
                  placement="left"
                  [ngbTooltip]="'general.edit' | translate"
                  (click)="onEditClick(paginatedItem, $event)"
                >
                  <fa-icon icon="edit" class="action" aria-hidden="true"></fa-icon>
                  <span class="sr-only">{{ "general.edit" | translate }}</span>
                </button>
                <button type="button" class="circle-icon-btn bg-transparent border-0 p-0" placement="left" [ngbTooltip]="'general.delete' | translate" (click)="onDeleteClick(paginatedItem, $event)">
                  <fa-icon icon="trash" class="action text-danger" aria-hidden="true"></fa-icon>
                  <span class="sr-only">{{ "general.delete" | translate }}</span>
                </button>
              </ng-container>

              @if (includeChevron) {
                <fa-icon aria-label="Select" icon="chevron-right" class="m-l-5" [ngbTooltip]="chevronTooltip" aria-hidden="true"></fa-icon>
              }
            </div>
          </div>
          @if (collapsible) {
            <div class="collapsible" [class.d-none]="collapsed">
              <ng-content select=".collapsible-content"></ng-content>
            </div>
          }
        </div>
      }

      <!-- Pagination at bottom -->
      @if (paginationPosition === "bottom") {
        <div class="pagination-container pagination-bottom">
          @if (showPaginationInfo) {
            <div class="pagination-info">
              {{ paginationInfo }}
            </div>
          }
          <div class="pagination-controls">
            <button
              class="btn btn-sm btn-outline-secondary pagination-btn"
              [disabled]="currentPage === 1"
              (click)="onPageChange(currentPage - 1)"
              [attr.aria-label]="'general.previous_page' | translate"
            >
              <fa-icon icon="chevron-left"></fa-icon>
            </button>

            @for (page of pageNumbers; track page) {
              <button
                class="btn btn-sm pagination-btn"
                [class.btn-primary]="page === currentPage"
                [class.btn-outline-secondary]="page !== currentPage"
                (click)="onPageChange(page)"
                [attr.aria-label]="('general.page' | translate) + ' ' + page"
              >
                {{ page }}
              </button>
            }

            <button
              class="btn btn-sm btn-outline-secondary pagination-btn"
              [disabled]="currentPage === totalPages"
              (click)="onPageChange(currentPage + 1)"
              [attr.aria-label]="'general.next_page' | translate"
            >
              <fa-icon icon="chevron-right"></fa-icon>
            </button>
          </div>
        </div>
      }
    </div>
  }
</div>
